"use client";

import React, { useEffect } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import {
  MessageSquare,
  MapPin,
  Phone,
  Globe,
  Send,
  Loader2,
} from "lucide-react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/navigation";
import { insertInquiry } from "@/modules/cms/inquiry.service";
import { InputField } from "@/components/common/FormFields/InputField";
import { SelectField } from "@/components/common/FormFields/SelectField";
import { TextAreaField } from "@/components/common/FormFields/TextAreaField";
import { INQUIRY_TYPES } from "@/helper/common";
import { createLookupObject } from "@/helper/helper";
import { useAppSelector } from "@/redux/hooks";
import { ISetting } from "../setting/settingModel";
import { FaWhatsapp } from "react-icons/fa";
import Link from "next/link";

const ContactSchema = Yup.object().shape({
  FullName: Yup.string()
    .required("Full name is required")
    .min(2, "Name too short"),
  MobileNo: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
  Email: Yup.string().email("Invalid email").required("Email is required"),
  InquiryType: Yup.string().required("Please select an inquiry type"),
  Remarks: Yup.string()
    .required("Message cannot be empty")
    .min(10, "Message too short"),
});

const INITIAL_VALUES = {
  FullName: "",
  MobileNo: "",
  Email: "",
  InquiryType: "",
  Remarks: "",
};

interface ContactClientProps {
  cmsData?: any[];
}

const ContactClient: React.FC<ContactClientProps> = ({ cmsData }) => {
  const router = useRouter();
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const [CMSData, setCMSData] = React.useState<any>({});
  const Settings: Record<string, ISetting> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISetting>,
  );

  const WhatsAppNo = Settings?.["WhatsAppNo"]?.SettingValue;
  const WhatsAppNo2 = Settings?.["WhatsAppNo2"]?.SettingValue;
  const CallTiming = Settings?.["CallTiming"]?.SettingValue;
  const Address = Settings?.["Address"]?.SettingValue;
  const IndiaMartLink = Settings?.["IndiaMartLink"]?.SettingValue;
  const GSTNo = Settings?.["GSTNo"]?.SettingValue;
  const phone = WhatsAppNo ? WhatsAppNo.replace(/\D+/g, "") : "";
  const whatsappLink = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent("Hello, I'd like to know more details.")}`
    : "#";
  const phone2 = WhatsAppNo2 ? WhatsAppNo2.replace(/\D+/g, "") : "";
  const whatsappLink2 = phone2
    ? `https://wa.me/${phone2}?text=${encodeURIComponent("Hello, I'd like to know more details.")}`
    : "#";

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const handleSubmit = async (
    values: typeof INITIAL_VALUES,
    { setSubmitting }: any,
  ) => {
    try {
      const payload = {
        FirstName: values.FullName,
        LastName: "",
        Address: "",
        Email: values.Email,
        MobileNo: values.MobileNo,
        CategoryIDs: "",
        InquiryType: values.InquiryType,
        Platform: "",
        OrderBudget: "",
        Remarks: values.Remarks,
      };

      const response = await insertInquiry(payload);
      if (
        response.Status === "success" ||
        response.ID ||
        response.Message === "success"
      ) {
        router.push("/thank-you");
      }
    } catch (error) {
      console.error("Submission Error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-black">
      <Navbar />

      {/* PAGE HERO */}
      {CMSData?.["Contact - Section 1 : Banner"] ? (
        <section
          className="banner-section border-b border-border-1 pt-18 pb-14 px-4 md:px-14 relative overflow-hidden"
          dangerouslySetInnerHTML={{
            __html: CMSData?.["Contact - Section 1 : Banner"]?.Template,
          }}
        />
      ) : (
        <section className="banner-section border-b border-border-1 pt-18 pb-14 px-4 md:px-14 relative overflow-hidden">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[120px] bg-gold-pool opacity-10 pointer-events-none" />
          <div className="flex items-center gap-3.5 text-[10px] font-semibold tracking-[0.28em] uppercase text-gold-core mb-4 before:h-px before:w-15 before:bg-gradient-to-r before:from-gold-dim before:to-transparent">
            Get in Touch
          </div>
          <h1 className="font-cormorant text-[clamp(36px,5vw,64px)] font-light text-text-1 leading-[1.1] mb-3">
            <em>Contact</em> Us
          </h1>
          <p className="text-sm text-text-2 font-light leading-relaxed max-w-lg">
            Our support team is available every day. Fastest response via
            WhatsApp.
          </p>
        </section>
      )}

      {/* CONTACT CONTENT */}
      <section className="py-20 px-4 md:px-14 bg-black">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Contact Form */}
          <div className="flex-1 space-y-8">
            <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-gold-core flex items-center gap-3.5">
              Send a Message
              <span className="h-px w-15 bg-gradient-to-r from-gold-dim to-transparent" />
            </div>

            <Formik
              initialValues={INITIAL_VALUES}
              validationSchema={ContactSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField
                      label="Your Name"
                      name="FullName"
                      placeholder="Enter Full name"
                      required
                      variant="dark"
                    />
                    <InputField
                      label="Phone / WhatsApp"
                      name="MobileNo"
                      placeholder="i.e. 9999999999"
                      required
                      variant="dark"
                    />
                  </div>
                  <InputField
                    label="Email Address"
                    name="Email"
                    type="email"
                    placeholder="i.e. example@gmail.com"
                    required
                    variant="dark"
                  />
                  <SelectField
                    label="Inquiry Type"
                    name="InquiryType"
                    options={INQUIRY_TYPES}
                    required
                    variant="dark"
                  />
                  <TextAreaField
                    label="Message / Remarks"
                    name="Remarks"
                    placeholder="How can we help you?"
                    required
                    variant="dark"
                    className="min-h-[120px]"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gold !w-full py-[15px] flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        Sending...{" "}
                        <Loader2 className="animate-spin" size={18} />
                      </>
                    ) : (
                      <>
                        Send Message <Send size={18} />
                      </>
                    )}
                  </button>
                </Form>
              )}
            </Formik>
          </div>

          {/* Contact Info Sidebar */}
          <div className="flex-1 space-y-10">
            <div className="space-y-6">
              <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-gold-core">
                Direct Contact
              </div>

              <div className="space-y-4">
                {WhatsAppNo && (
                  <div className="bg-black-2 border border-border-1 border-l-2 border-l-gold-dim p-5 px-[22px]">
                    <div className="text-[10px] text-gold-core font-semibold tracking-[0.16em] uppercase mb-2">
                      WhatsApp Support 1
                    </div>
                    <div className="text-md text-text-1 font-medium">
                      <Link
                        href={`tel:${phone}`}
                        className="cursor-pointer hover:text-gold-core transition-colors"
                      >
                        {WhatsAppNo}
                      </Link>
                    </div>
                    {CallTiming && (
                      <div className="text-sm text-text-3 mt-1.5 font-light leading-[1.6]">
                        {CallTiming}
                      </div>
                    )}
                  </div>
                )}
                {WhatsAppNo2 && (
                  <div className="bg-black-2 border border-border-1 border-l-2 border-l-gold-dim p-5 px-[22px]">
                    <div className="text-[10px] text-gold-core font-semibold tracking-[0.16em] uppercase mb-2">
                      WhatsApp Support 2
                    </div>
                    <div className="text-md text-text-1 font-medium">
                      <Link
                        href={`tel:${phone2}`}
                        className="cursor-pointer hover:text-gold-core transition-colors"
                      >
                        {WhatsAppNo2}
                      </Link>
                    </div>
                    {CallTiming && (
                      <div className="text-sm text-text-3 mt-1.5 font-light leading-[1.6]">
                        {CallTiming}
                      </div>
                    )}
                  </div>
                )}
                {Address && (
                  <div className="bg-black-2 border border-border-1 border-l-2 border-l-gold-dim p-5 px-[22px]">
                    <div className="text-[10px] text-gold-core font-semibold tracking-[0.16em] uppercase mb-2">
                      Business Location
                    </div>
                    <div className="text-md text-text-1 font-medium">
                      {Address}
                    </div>
                    <div className="text-sm text-text-3 mt-1.5 font-light leading-[1.6]">
                      Factory-direct wholesale supplier
                    </div>
                  </div>
                )}
                {GSTNo && (
                  <div className="bg-black-2 border border-border-1 border-l-2 border-l-gold-dim p-5 px-[22px]">
                    <div className="text-[10px] text-gold-core font-semibold tracking-[0.16em] uppercase mb-2">
                      GST No
                    </div>
                    <div className="text-md text-text-1 font-medium">
                      {GSTNo}
                    </div>
                    <div className="text-sm text-text-3 mt-1.5 font-light leading-[1.6]">
                      Verified & Regular GST Tax Payer
                    </div>
                  </div>
                )}
                {IndiaMartLink && (
                  <div className="bg-black-2 border border-border-1 border-l-2 border-l-gold-dim p-5 px-[22px]">
                    <div className="text-[10px] text-gold-core font-semibold tracking-[0.16em] uppercase mb-2">
                      IndiaMart
                    </div>
                    <div className="text-md text-text-1 font-medium">
                      <Link
                        href={IndiaMartLink}
                        target="_blank"
                        className="cursor-pointer hover:text-gold-core transition-colors"
                      >
                        {IndiaMartLink}
                      </Link>
                    </div>
                    <div className="text-sm text-text-3 mt-1.5 font-light leading-[1.6]">
                      Verified business profile — check our reviews
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(WhatsAppNo || WhatsAppNo2) && (
              <div className="flex gap-3">
                {WhatsAppNo && (
                  <Link
                    href={whatsappLink}
                    target="_blank"
                    className="btn-gold flex-1 text-center flex items-center gap-2 justify-center cursor-pointer"
                  >
                    <FaWhatsapp className="text-lg" /> WhatsApp 1
                  </Link>
                )}
                {WhatsAppNo2 && (
                  <Link
                    href={whatsappLink2}
                    target="_blank"
                    className="btn-ghost flex-1 text-center flex items-center gap-2 justify-center cursor-pointer"
                  >
                    <FaWhatsapp className="text-lg" /> WhatsApp 2
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="gold-divider" />
      <Footer />
    </main>
  );
};

export default ContactClient;
